import { generateContent } from './geminiAI';

// Content moderation categories
export const MODERATION_CATEGORIES = {
  INAPPROPRIATE: 'inappropriate',
  COPYRIGHT: 'copyright',
  SPAM: 'spam',
  QUALITY: 'quality',
  DUPLICATE: 'duplicate',
  SAFE: 'safe'
};

// Moderation severity levels
export const SEVERITY_LEVELS = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

/**
 * Review game content for inappropriate material, copyright violations, and quality issues
 * @param {Object} gameData - The game data to review
 * @returns {Object} Moderation result with category, severity, and recommendations
 */
export const reviewGameContent = async (gameData) => {
  try {
    const { title, description, quarter1, quarter2, quarter3, quarter4, halftime, final } = gameData;
    
    // Prepare content for review
    const contentToReview = {
      title: title || '',
      description: description || '',
      questions: [
        ...(quarter1 || []),
        ...(quarter2 || []),
        ...(quarter3 || []),
        ...(quarter4 || []),
        halftime ? [halftime] : [],
        final ? [final] : []
      ].flat().filter(q => q)
    };

    const prompt = `
You are a content moderator for a trivia game platform. Your job is to ONLY flag GENUINELY PROBLEMATIC content.

IMPORTANT: This is a TRIVIA platform. Almost everything is acceptable. Only flag content that is:
1. Explicitly illegal (drug manufacturing, violence instructions, etc.)
2. Hate speech or extreme discrimination
3. Explicit sexual content
4. Direct copyright violations (exact quotes, song lyrics, etc.)

EVERYTHING ELSE IS SAFE, including:
- Science questions (chemistry, physics, biology, etc.)
- History questions (wars, politics, etc.)
- Pop culture (movies, music, celebrities, etc.)
- Sports (including alcohol brands like Guinness)
- Geography (countries, cities, landmarks)
- Literature and mythology
- Technology and current events
- Food and drinks (including alcohol)
- Any educational content

Game Title: "${contentToReview.title}"
Description: "${contentToReview.description}"

Questions to Review:
${contentToReview.questions.map((q, i) => `
Question ${i + 1}:
- Category: ${q.category || 'N/A'}
- Question: ${q.question || 'N/A'}
- Question Instruction: ${q.questionInstruction || 'N/A'}
- Left Column: ${q.leftColumn || 'N/A'}
- Right Column: ${q.rightColumn || 'N/A'}
- Answer: ${q.answer || 'N/A'}
`).join('\n')}

Respond with a JSON object in this exact format:
{
  "category": "safe|inappropriate|copyright|spam|quality|duplicate",
  "severity": "low|medium|high|critical",
  "approved": true|false,
  "issues": [
    {
      "type": "inappropriate|copyright|spam|quality|duplicate",
      "description": "Specific issue description with exact content that was flagged",
      "suggestion": "How to fix this issue",
      "flaggedContent": "The exact text or content that triggered this flag"
    }
  ],
  "recommendations": "Overall recommendations for improvement",
  "confidence": 0.95,
  "flaggedItems": [
    {
      "field": "title|description|question|answer|instruction|leftColumn|rightColumn",
      "content": "The exact content that was flagged",
      "reason": "Why this content was flagged"
    }
  ]
}

CRITICAL INSTRUCTIONS:
- If the content is educational, informative, or trivia-related, mark it as "safe"
- Only flag content that is explicitly illegal, hateful, or sexually explicit
- When in doubt, APPROVE the content
- Be extremely permissive - this is an educational trivia platform

Examples of what to APPROVE:
- "What element has the highest melting point?" (science - APPROVE)
- "Who is Beyoncé?" (pop culture - APPROVE)
- "What is bourbon?" (alcohol education - APPROVE)
- "What is the tallest volcano?" (geography - APPROVE)
- "What is Guinness?" (alcohol brand - APPROVE)

Examples of what to FLAG:
- "How to make methamphetamine" (illegal activity - FLAG)
- "Racial slurs for different groups" (hate speech - FLAG)
- "Explicit sexual content" (adult content - FLAG)

Be extremely permissive. If you're not 100% sure it's problematic, approve it.
`;

    const response = await generateContent(prompt);
    
    // Debug: Log the raw AI response
    console.log('Raw AI moderation response:', response);
    
    // Parse the JSON response
    let moderationResult;
    try {
      moderationResult = JSON.parse(response);
      console.log('Parsed moderation result:', JSON.stringify(moderationResult, null, 2));
    } catch (parseError) {
      console.error('Failed to parse AI moderation response:', parseError);
      console.error('Raw AI response:', response);
      // Fallback to manual review if AI response is malformed
      return {
        category: MODERATION_CATEGORIES.QUALITY,
        severity: SEVERITY_LEVELS.MEDIUM,
        approved: false,
        issues: [{
          type: 'quality',
          description: 'AI moderation response was malformed',
          suggestion: 'Manual review required'
        }],
        recommendations: 'This content requires manual review due to AI processing error.',
        confidence: 0.5
      };
    }

    // Ensure we have flaggedItems even if AI doesn't provide them
    if (!moderationResult.flaggedItems && moderationResult.category !== MODERATION_CATEGORIES.SAFE) {
      moderationResult.flaggedItems = [];
      
      // Add title if it exists and might be problematic
      if (contentToReview.title && contentToReview.title.trim()) {
        moderationResult.flaggedItems.push({
          field: 'title',
          content: contentToReview.title,
          reason: `Title flagged for ${moderationResult.category} content`
        });
      }
      
      // Add description if it exists and might be problematic
      if (contentToReview.description && contentToReview.description.trim()) {
        moderationResult.flaggedItems.push({
          field: 'description',
          content: contentToReview.description,
          reason: `Description flagged for ${moderationResult.category} content`
        });
      }
      
      // Add questions if they exist and might be problematic
      contentToReview.questions.forEach((question, index) => {
        if (question.question && question.question.trim()) {
          moderationResult.flaggedItems.push({
            field: `question_${index + 1}`,
            content: question.question,
            reason: `Question ${index + 1} flagged for ${moderationResult.category} content`
          });
        }
        if (question.answer && question.answer.trim()) {
          moderationResult.flaggedItems.push({
            field: `answer_${index + 1}`,
            content: question.answer,
            reason: `Answer ${index + 1} flagged for ${moderationResult.category} content`
          });
        }
      });
    }

    return {
      ...moderationResult,
      reviewedAt: new Date(),
      contentType: 'game',
      contentId: gameData.id || 'new'
    };

  } catch (error) {
    console.error('Error in game content moderation:', error);
    return {
      category: MODERATION_CATEGORIES.QUALITY,
      severity: SEVERITY_LEVELS.MEDIUM,
      approved: false,
      issues: [{
        type: 'quality',
        description: 'Moderation system error',
        suggestion: 'Manual review required'
      }],
      recommendations: 'This content requires manual review due to system error.',
      confidence: 0.3,
      reviewedAt: new Date(),
      contentType: 'game',
      contentId: gameData.id || 'new',
      error: error.message
    };
  }
};

/**
 * Review slide content for inappropriate material and quality issues
 * @param {Object} slideData - The slide data to review
 * @returns {Object} Moderation result with category, severity, and recommendations
 */
export const reviewSlideContent = async (slideData) => {
  try {
    const { title, content, slideType, targetType, targetValue } = slideData;
    
    const prompt = `
You are a content moderator for a trivia game platform. Review the following slide content for:

1. INAPPROPRIATE CONTENT: Profanity, adult content, hate speech, violence, drugs, etc.
2. COPYRIGHT VIOLATIONS: Direct quotes, song lyrics, movie lines, book excerpts without permission
3. SPAM/DUPLICATE: Repetitive, low-quality, or duplicate content
4. QUALITY ISSUES: Poor grammar, unclear messaging, inappropriate targeting

Slide Type: ${slideType}
Target Type: ${targetType}
Target Value: ${targetValue}

Title: "${title}"
Content: "${content}"

Respond with a JSON object in this exact format:
{
  "category": "safe|inappropriate|copyright|spam|quality|duplicate",
  "severity": "low|medium|high|critical",
  "approved": true|false,
  "issues": [
    {
      "type": "inappropriate|copyright|spam|quality|duplicate",
      "description": "Specific issue description",
      "suggestion": "How to fix this issue"
    }
  ],
  "recommendations": "Overall recommendations for improvement",
  "confidence": 0.95
}

Be strict but fair. If in doubt, flag for manual review.
`;

    const response = await generateContent(prompt);
    
    let moderationResult;
    try {
      moderationResult = JSON.parse(response);
    } catch (parseError) {
      console.error('Failed to parse AI slide moderation response:', parseError);
      return {
        category: MODERATION_CATEGORIES.QUALITY,
        severity: SEVERITY_LEVELS.MEDIUM,
        approved: false,
        issues: [{
          type: 'quality',
          description: 'AI moderation response was malformed',
          suggestion: 'Manual review required'
        }],
        recommendations: 'This slide requires manual review due to AI processing error.',
        confidence: 0.5
      };
    }

    return {
      ...moderationResult,
      reviewedAt: new Date(),
      contentType: 'slide',
      contentId: slideData.id || 'new'
    };

  } catch (error) {
    console.error('Error in slide content moderation:', error);
    return {
      category: MODERATION_CATEGORIES.QUALITY,
      severity: SEVERITY_LEVELS.MEDIUM,
      approved: false,
      issues: [{
        type: 'quality',
        description: 'Moderation system error',
        suggestion: 'Manual review required'
      }],
      recommendations: 'This slide requires manual review due to system error.',
      confidence: 0.3,
      reviewedAt: new Date(),
      contentType: 'slide',
      contentId: slideData.id || 'new',
      error: error.message
    };
  }
};

/**
 * Review user profile content for inappropriate material
 * @param {Object} profileData - The profile data to review
 * @returns {Object} Moderation result
 */
export const reviewProfileContent = async (profileData) => {
  try {
    const { fullName, bio, teamName } = profileData;
    
    const prompt = `
You are a content moderator for a trivia game platform. Review the following profile content for:

1. INAPPROPRIATE CONTENT: Profanity, adult content, hate speech, violence, drugs, etc.
2. SPAM: Repetitive, low-quality, or inappropriate content
3. QUALITY ISSUES: Poor grammar, unclear messaging

Full Name: "${fullName || ''}"
Bio: "${bio || ''}"
Team Name: "${teamName || ''}"

Respond with a JSON object in this exact format:
{
  "category": "safe|inappropriate|spam|quality",
  "severity": "low|medium|high|critical",
  "approved": true|false,
  "issues": [
    {
      "type": "inappropriate|spam|quality",
      "description": "Specific issue description",
      "suggestion": "How to fix this issue"
    }
  ],
  "recommendations": "Overall recommendations for improvement",
  "confidence": 0.95
}

Be strict but fair. If in doubt, flag for manual review.
`;

    const response = await generateContent(prompt);
    
    let moderationResult;
    try {
      moderationResult = JSON.parse(response);
    } catch (parseError) {
      console.error('Failed to parse AI profile moderation response:', parseError);
      return {
        category: MODERATION_CATEGORIES.QUALITY,
        severity: SEVERITY_LEVELS.MEDIUM,
        approved: false,
        issues: [{
          type: 'quality',
          description: 'AI moderation response was malformed',
          suggestion: 'Manual review required'
        }],
        recommendations: 'This profile requires manual review due to AI processing error.',
        confidence: 0.5
      };
    }

    return {
      ...moderationResult,
      reviewedAt: new Date(),
      contentType: 'profile',
      contentId: profileData.id || 'new'
    };

  } catch (error) {
    console.error('Error in profile content moderation:', error);
    return {
      category: MODERATION_CATEGORIES.QUALITY,
      severity: SEVERITY_LEVELS.MEDIUM,
      approved: false,
      issues: [{
        type: 'quality',
        description: 'Moderation system error',
        suggestion: 'Manual review required'
      }],
      recommendations: 'This profile requires manual review due to system error.',
      confidence: 0.3,
      reviewedAt: new Date(),
      contentType: 'profile',
      contentId: profileData.id || 'new',
      error: error.message
    };
  }
};

/**
 * Get moderation status display information
 * @param {Object} moderationResult - The moderation result
 * @returns {Object} Display information for the moderation status
 */
export const getModerationDisplay = (moderationResult) => {
  const { category, severity, approved, confidence } = moderationResult;
  
  const statusConfig = {
    [MODERATION_CATEGORIES.SAFE]: {
      icon: '✅',
      color: 'green',
      text: 'Approved',
      bgColor: 'bg-green-100',
      textColor: 'text-green-800'
    },
    [MODERATION_CATEGORIES.INAPPROPRIATE]: {
      icon: '🚫',
      color: 'red',
      text: 'Inappropriate Content',
      bgColor: 'bg-red-100',
      textColor: 'text-red-800'
    },
    [MODERATION_CATEGORIES.COPYRIGHT]: {
      icon: '©️',
      color: 'orange',
      text: 'Copyright Issue',
      bgColor: 'bg-orange-100',
      textColor: 'text-orange-800'
    },
    [MODERATION_CATEGORIES.SPAM]: {
      icon: '📧',
      color: 'yellow',
      text: 'Spam Content',
      bgColor: 'bg-yellow-100',
      textColor: 'text-yellow-800'
    },
    [MODERATION_CATEGORIES.QUALITY]: {
      icon: '⚠️',
      color: 'blue',
      text: 'Quality Issue',
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-800'
    },
    [MODERATION_CATEGORIES.DUPLICATE]: {
      icon: '🔄',
      color: 'purple',
      text: 'Duplicate Content',
      bgColor: 'bg-purple-100',
      textColor: 'text-purple-800'
    }
  };

  const config = statusConfig[category] || statusConfig[MODERATION_CATEGORIES.QUALITY];
  
  return {
    ...config,
    approved,
    confidence: Math.round(confidence * 100),
    severity: severity.toUpperCase()
  };
};

/**
 * Check if content should be auto-approved based on moderation result
 * @param {Object} moderationResult - The moderation result
 * @returns {boolean} Whether content should be auto-approved
 */
export const shouldAutoApprove = (moderationResult) => {
  const { category, severity, confidence } = moderationResult;
  
  // Auto-approve if:
  // 1. Category is safe AND
  // 2. Severity is low AND  
  // 3. Confidence is high
  return category === MODERATION_CATEGORIES.SAFE && 
         severity === SEVERITY_LEVELS.LOW && 
         confidence >= 0.8;
};

/**
 * Check if content should be auto-rejected based on moderation result
 * @param {Object} moderationResult - The moderation result
 * @returns {boolean} Whether content should be auto-rejected
 */
export const shouldAutoReject = (moderationResult) => {
  const { category, severity, confidence } = moderationResult;
  
  // Auto-reject ONLY for:
  // 1. Category is inappropriate AND severity is critical OR
  // 2. Severity is critical OR
  // 3. Extremely high confidence in genuinely illegal/harmful content
  return (category === MODERATION_CATEGORIES.INAPPROPRIATE && 
          severity === SEVERITY_LEVELS.CRITICAL) ||
         severity === SEVERITY_LEVELS.CRITICAL ||
         (confidence >= 0.98 && category === MODERATION_CATEGORIES.INAPPROPRIATE && severity === SEVERITY_LEVELS.HIGH);
};

/**
 * Check if the moderation result might be a false positive
 * @param {Object} moderationResult - The moderation result
 * @param {Object} contentToReview - The original content that was reviewed
 * @returns {boolean} Whether this might be a false positive
 */
export const isPotentialFalsePositive = (moderationResult, contentToReview) => {
  const { category, confidence, flaggedItems } = moderationResult;
  
  // If confidence is low and category is inappropriate, it might be a false positive
  if (category === MODERATION_CATEGORIES.INAPPROPRIATE && confidence < 0.8) {
    return true;
  }
  
  // If flaggedItems contains mostly normal trivia content, it might be a false positive
  if (flaggedItems && flaggedItems.length > 0) {
    const normalTriviaKeywords = [
      'science', 'history', 'geography', 'sports', 'movies', 'music', 'literature',
      'art', 'politics', 'technology', 'nature', 'animals', 'plants', 'chemistry',
      'physics', 'mathematics', 'astronomy', 'biology', 'medicine', 'economics',
      'philosophy', 'religion', 'mythology', 'folklore', 'architecture', 'fashion',
      'food', 'drinks', 'travel', 'countries', 'cities', 'rivers', 'mountains',
      'oceans', 'continents', 'planets', 'stars', 'galaxies', 'elements', 'compounds',
      'molecules', 'atoms', 'cells', 'organs', 'systems', 'species', 'genus',
      'family', 'order', 'class', 'phylum', 'kingdom', 'domain', 'era', 'period',
      'epoch', 'age', 'century', 'decade', 'year', 'month', 'day', 'hour', 'minute',
      'second', 'millisecond', 'microsecond', 'nanosecond', 'picosecond'
    ];
    
    let normalContentCount = 0;
    flaggedItems.forEach(item => {
      const content = item.content.toLowerCase();
      if (normalTriviaKeywords.some(keyword => content.includes(keyword))) {
        normalContentCount++;
      }
    });
    
    // If more than 70% of flagged content contains normal trivia keywords, it might be a false positive
    if (normalContentCount / flaggedItems.length > 0.7) {
      return true;
    }
  }
  
  return false;
}; 